import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { browseBlobEmails, searchAllEmails, getContainers } from '../api';
import EmailList from '../components/EmailList';
import Pagination from '../components/Pagination';

function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null);

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  const [loadingYears, setLoadingYears] = useState(true);
  const [searchMeta, setSearchMeta] = useState(null);

  // Local form state (committed to URL on Search click)
  const [formSender, setFormSender] = useState(searchParams.get('sender') || '');
  const [formTo, setFormTo] = useState(searchParams.get('to') || '');
  const [formCc, setFormCc] = useState(searchParams.get('cc') || '');
  const [formSubject, setFormSubject] = useState(searchParams.get('subject') || '');
  const [formKeywords, setFormKeywords] = useState(searchParams.get('q') || '');
  const [formDateFrom, setFormDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [formDateTo, setFormDateTo] = useState(searchParams.get('dateTo') || '');
  const [formFolder, setFormFolder] = useState(searchParams.get('folder') || '');
  const [formAttachments, setFormAttachments] = useState(searchParams.get('hasAttachments') === 'true');

  // Fetch available years (containers) on mount
  useEffect(() => {
    async function fetchYears() {
      try {
        const data = await getContainers();
        setAvailableYears(data.containers || []);
      } catch (err) {
        console.error('Failed to load available years:', err);
        setAvailableYears(['2022', '2021', '2020', '2019']);
      } finally {
        setLoadingYears(false);
      }
    }
    fetchYears();
  }, []);

  // Get filters from URL
  const filters = {
    q: searchParams.get('q') || '',
    page: parseInt(searchParams.get('page')) || 1,
    container: searchParams.get('container') || '',
    sender: searchParams.get('sender') || '',
    to: searchParams.get('to') || '',
    cc: searchParams.get('cc') || '',
    subject: searchParams.get('subject') || '',
    folder: searchParams.get('folder') || '',
    hasAttachments: searchParams.get('hasAttachments') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || ''
  };

  // Search mode: any text search field is filled
  const isSearchMode = !!(filters.q || filters.subject || filters.to || filters.cc || filters.sender);
  const hasYearSelected = !!filters.container;
  const hasDateFilters = !!filters.dateFrom || !!filters.dateTo;

  // Load data when URL params change
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (filters.container || isSearchMode || hasDateFilters) {
        if (isSearchMode) {
          performSearch();
        } else if (hasYearSelected || hasDateFilters) {
          loadEmails();
        }
      }
      return;
    }

    if (isSearchMode) {
      performSearch();
    } else if (hasYearSelected || hasDateFilters) {
      loadEmails();
    }
  }, [searchParams]);

  function cancelOngoingRequests() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  // Browse mode
  async function loadEmails() {
    if (!filters.container && !filters.dateFrom && !filters.dateTo) return;

    cancelOngoingRequests();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setHasLoadedOnce(true);

    try {
      const params = {
        page: filters.page,
        limit: 50
      };
      if (filters.container) params.container = filters.container;
      if (filters.sender) params.sender = filters.sender;
      if (filters.folder) params.folder = filters.folder;
      if (filters.hasAttachments) params.hasAttachments = filters.hasAttachments;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const data = await browseBlobEmails(params, controller.signal);

      if (!controller.signal.aborted) {
        setResults({
          data: data.data,
          mode: 'browse',
          pagination: data.pagination
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!controller.signal.aborted) {
        setError(err.message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }

  // Search mode
  async function performSearch() {
    cancelOngoingRequests();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);
    setHasLoadedOnce(true);
    setSearchMeta(null);

    try {
      const params = {
        limit: 100,
        page: filters.page
      };

      // Field-specific search params
      if (filters.q) params.q = filters.q;
      if (filters.subject) params.subject = filters.subject;
      if (filters.sender) params.senderEmail = filters.sender;
      if (filters.to) params.to = filters.to;
      if (filters.cc) params.cc = filters.cc;

      // Filter params
      if (filters.container) params.container = filters.container;
      if (filters.folder) params.folder = filters.folder;
      if (filters.hasAttachments) params.hasAttachments = filters.hasAttachments;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const data = await searchAllEmails(params, controller.signal);

      if (!controller.signal.aborted) {
        setResults({
          data: data.data,
          count: data.totalFound || data.count,
          mode: 'search',
          pagination: data.pagination,
          facets: data.facets
        });
        setSearchMeta(data._meta);
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      if (!controller.signal.aborted) {
        setError(err.message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }

  // Year dropdown changes immediately (browse behavior)
  function handleYearChange(value) {
    if (!value) {
      cancelOngoingRequests();
      setLoading(false);
      setSearchParams(new URLSearchParams());
      setResults(null);
      setError(null);
      setHasLoadedOnce(false);
      return;
    }

    const params = new URLSearchParams(searchParams);
    params.set('container', value);
    params.delete('page');
    setSearchParams(params);
  }

  // Search form submit — commits all form fields to URL
  function handleSearch(e) {
    e.preventDefault();
    const params = new URLSearchParams();

    // Keep year if selected
    if (filters.container) params.set('container', filters.container);

    // Commit form fields
    if (formKeywords.trim()) params.set('q', formKeywords.trim());
    if (formSender.trim()) params.set('sender', formSender.trim());
    if (formTo.trim()) params.set('to', formTo.trim());
    if (formCc.trim()) params.set('cc', formCc.trim());
    if (formSubject.trim()) params.set('subject', formSubject.trim());
    if (formFolder.trim()) params.set('folder', formFolder.trim());
    if (formDateFrom) params.set('dateFrom', formDateFrom);
    if (formDateTo) params.set('dateTo', formDateTo);
    if (formAttachments) params.set('hasAttachments', 'true');

    setSearchParams(params);
  }

  function clearFilters() {
    cancelOngoingRequests();
    setLoading(false);
    // Reset form state
    setFormSender('');
    setFormTo('');
    setFormCc('');
    setFormSubject('');
    setFormKeywords('');
    setFormDateFrom('');
    setFormDateTo('');
    setFormFolder('');
    setFormAttachments(false);
    // Reset URL
    setSearchParams(new URLSearchParams());
    setResults(null);
    setError(null);
    setHasLoadedOnce(false);
  }

  function handlePageChange(newPage) {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  }

  const hasActiveFilters = filters.sender || filters.folder || filters.hasAttachments || filters.q || filters.container || filters.dateFrom || filters.dateTo || filters.to || filters.cc || filters.subject;

  // Build search summary for results header
  const searchSummaryParts = [];
  if (filters.q) searchSummaryParts.push(`"${filters.q}"`);
  if (filters.sender) searchSummaryParts.push(`From: ${filters.sender}`);
  if (filters.to) searchSummaryParts.push(`To: ${filters.to}`);
  if (filters.cc) searchSummaryParts.push(`Cc: ${filters.cc}`);
  if (filters.subject) searchSummaryParts.push(`Subject: ${filters.subject}`);

  const inputClass = "w-full px-3 py-2 border-b border-gray-300 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors";
  const labelClass = "text-sm font-medium text-gray-600 whitespace-nowrap";

  return (
    <div className="space-y-4">
      {/* Search Form Card — Outlook-style layout */}
      <div className="bg-white rounded-lg shadow">
        <form onSubmit={handleSearch}>
          <div className="p-5">
            <div className="grid grid-cols-[100px_1fr] gap-x-6 gap-y-1 items-center">
              {/* Keywords */}
              <label className={labelClass}>Keywords</label>
              <input
                type="text"
                value={formKeywords}
                onChange={(e) => setFormKeywords(e.target.value)}
                placeholder=""
                className={inputClass}
              />

              {/* Search in / Year */}
              <label className={labelClass}>Search in</label>
              <div className="flex items-center gap-3">
                <select
                  value={filters.container}
                  onChange={(e) => handleYearChange(e.target.value)}
                  disabled={loadingYears}
                  className="flex-1 px-3 py-2 border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:border-blue-500 focus:outline-none disabled:opacity-50 appearance-none cursor-pointer"
                >
                  <option value="">{loadingYears ? 'Loading...' : 'All years'}</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                {/* Folder */}
                <input
                  type="text"
                  value={formFolder}
                  onChange={(e) => setFormFolder(e.target.value)}
                  placeholder="All folders"
                  className={`flex-1 ${inputClass}`}
                />
              </div>

              {/* From */}
              <label className={labelClass}>From</label>
              <input
                type="text"
                value={formSender}
                onChange={(e) => setFormSender(e.target.value)}
                placeholder=""
                className={inputClass}
              />

              {/* To */}
              <label className={labelClass}>To</label>
              <input
                type="text"
                value={formTo}
                onChange={(e) => setFormTo(e.target.value)}
                placeholder=""
                className={inputClass}
              />

              {/* Cc */}
              <label className={labelClass}>Cc</label>
              <input
                type="text"
                value={formCc}
                onChange={(e) => setFormCc(e.target.value)}
                placeholder=""
                className={inputClass}
              />

              {/* Subject */}
              <label className={labelClass}>Subject</label>
              <input
                type="text"
                value={formSubject}
                onChange={(e) => setFormSubject(e.target.value)}
                placeholder=""
                className={inputClass}
              />

              {/* Date */}
              <label className={labelClass}>Date</label>
              <div className="flex items-center gap-3">
                <input
                  type="date"
                  value={formDateFrom}
                  onChange={(e) => setFormDateFrom(e.target.value)}
                  className="flex-1 px-3 py-2 border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                  placeholder="Start"
                />
                <input
                  type="date"
                  value={formDateTo}
                  onChange={(e) => setFormDateTo(e.target.value)}
                  className="flex-1 px-3 py-2 border-b border-gray-300 bg-transparent text-sm text-gray-900 focus:border-blue-500 focus:outline-none"
                  placeholder="End"
                />
              </div>

              {/* Attachments */}
              <label className={labelClass}>Attachments</label>
              <div className="py-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formAttachments}
                    onChange={(e) => setFormAttachments(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">Has attachments</span>
                </label>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching...
                </span>
              ) : 'Search'}
            </button>
            {(hasActiveFilters || loading) && (
              <button
                type="button"
                onClick={clearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded hover:bg-white transition-colors"
              >
                Clear filters
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Initial State */}
      {!loading && !hasLoadedOnce && !results && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className="text-gray-500 text-lg">Select a year to browse emails or use the search fields</p>
          <p className="text-sm text-gray-400 mt-2">Fill in any combination of fields and click Search</p>
        </div>
      )}

      {/* Results */}
      {!loading && results && (
        <>
          {/* Results Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {isSearchMode ? (
                  <>
                    Found <span className="font-medium text-gray-900">{results.count || results.data?.length || 0}</span> results
                    {searchSummaryParts.length > 0 && (
                      <> for {searchSummaryParts.join(', ')}</>
                    )}
                    {filters.container && <span> in {filters.container}</span>}
                  </>
                ) : (
                  <>
                    Showing <span className="font-medium text-gray-900">{results.data?.length || 0}</span>
                    {results.pagination?.total > 0 && (
                      <> of <span className="font-medium text-gray-900">{results.pagination.total.toLocaleString()}</span></>
                    )} emails
                    {!filters.container && hasDateFilters && ' across all years'}
                    {results.pagination?.hasMore && ' (more available)'}
                  </>
                )}
              </p>
            </div>
            {searchMeta?.source && (
              <p className="text-xs text-gray-400">
                {searchMeta.source === 'azure-search' ? 'Azure Search' : 'Blob scan'} &middot; {searchMeta.durationMs}ms
              </p>
            )}
          </div>

          {/* Email List */}
          {results.data?.length > 0 ? (
            <>
              <EmailList
                emails={results.data}
                showSnippet={false}
                onEmailClick={(email) => {
                  const params = new URLSearchParams({
                    blobPath: email.source_blob,
                    container: email.source_container || filters.container
                  });
                  navigate(`/email/${encodeURIComponent(email.id)}?${params.toString()}`);
                }}
              />
              {results.pagination && (
                <Pagination
                  page={results.pagination.page || 1}
                  totalPages={results.pagination.totalPages || 1}
                  onPageChange={handlePageChange}
                  hasMore={results.pagination.hasMore || false}
                />
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow p-8 text-center">
              {isSearchMode ? (
                <>
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500">No results found</p>
                  <p className="text-sm text-gray-400 mt-2">Try different search terms or adjust your filters.</p>
                </>
              ) : (
                <>
                  <p className="text-gray-500">No emails found in {filters.container}</p>
                  <p className="text-sm text-gray-400 mt-2">Try selecting a different year.</p>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BrowsePage;
